/**
 * NewShootCard.tsx — 2004va
 *
 * Shown when a creator lands on UploadPage with no active session.
 * Displays the inferred shoot type from event + history.
 * One tap to confirm, or free text to add context / override.
 *
 * Design principles:
 * - No forms, no dropdowns — just a card and an optional text field
 * - Inference is the default, amendment is the exception
 * - Puppy dog: get them shooting fast, context is optional
 */

import { useState } from 'react'
import { Camera, ChevronRight, Pencil, X } from 'lucide-react'
import {
  useSessionStore,
  createShootSession,
  inferShootType,
  SHOOT_TYPE_LABELS,
} from '@/stores/sessionStore'
import { useAuthStore } from '@/stores/authStore'
import { Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'
import type { Event } from '@/types'

interface Props {
  event: Event | null
}

export function NewShootCard({ event }: Props) {
  const { participant } = useAuthStore()
  const { setSession }  = useSessionStore()

  const inferred = inferShootType(
    event?.tags ?? [],
    event?.name ?? '',
    participant?.role ?? 'creator'
  )

  const [adding,  setAdding]  = useState(false)   // show free text field
  const [brief,   setBrief]   = useState('')
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')

  async function handleStart(briefOverride?: string) {
    if (!participant) return
    setLoading(true)
    setError('')

    try {
      const session = await createShootSession({
        participantId: participant.id,
        eventId:       event?.id ?? null,
        eventName:     event?.name ?? null,
        venueName:     event?.venue ?? null,
        inferredType:  inferred,
        confirmedType: inferred,
        sessionBrief:  briefOverride?.trim() || null,
      })
      setSession(session)
    } catch (e) {
      setError('Could not start session — try again')
      setLoading(false)
    }
  }

  const label = SHOOT_TYPE_LABELS[inferred]

  return (
    <div className="animate-fade-in space-y-4">
      {/* ── Inference card ── */}
      <div className="bg-room-surface border border-room-border rounded-2xl p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-room-accent/10 border border-room-accent/20 flex items-center justify-center flex-shrink-0">
            <Camera size={18} className="text-room-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-room-muted uppercase tracking-widest font-semibold mb-0.5">
              New Shoot
            </p>
            {event ? (
              <>
                <p className="text-white font-semibold text-sm truncate">{event.name}</p>
                {event.venue && (
                  <p className="text-room-muted text-xs">{event.venue}</p>
                )}
              </>
            ) : (
              <p className="text-room-muted text-sm">No event — personal shoot</p>
            )}
          </div>
        </div>

        {/* Inferred type pill */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-room-muted">Inferred purpose</span>
          <span className="px-2.5 py-0.5 rounded-full bg-room-accent/15 border border-room-accent/25 text-room-accent text-xs font-semibold">
            {label}
          </span>
          <span className="text-xs text-room-muted ml-auto">Based on event + your history</span>
        </div>

        {/* Context input — shown when creator taps "Add context" */}
        {adding ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                autoFocus
                type="text"
                value={brief}
                onChange={e => setBrief(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && brief.trim()) handleStart(brief)
                  if (e.key === 'Escape') { setAdding(false); setBrief('') }
                }}
                placeholder="e.g. opportunity shot of Gris, personal project"
                className={cn(
                  'flex-1 bg-black/30 border border-room-border rounded-xl px-3 py-2.5',
                  'text-sm text-white placeholder:text-room-muted',
                  'focus:outline-none focus:border-room-accent/50 transition-colors w-full'
                )}
                maxLength={200}
              />
              <button
                type="button"
                onClick={() => { setAdding(false); setBrief('') }}
                className="text-room-muted hover:text-white transition-colors p-1"
              >
                <X size={14} />
              </button>
            </div>
            <p className="text-xs text-room-muted">
              Tell the AI what you're shooting — free text, your own words
            </p>
          </div>
        ) : null}

        {error && (
          <p className="text-xs text-room-error">{error}</p>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {/* Add context — secondary */}
          {!adding && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              disabled={loading}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2.5 rounded-xl',
                'border border-room-border text-room-muted hover:text-white hover:border-white/20',
                'text-xs font-semibold transition-all',
                'disabled:opacity-40 disabled:cursor-not-allowed'
              )}
            >
              <Pencil size={12} />
              Add context
            </button>
          )}

          {/* Start shooting — primary */}
          <button
            type="button"
            onClick={() => handleStart(brief || undefined)}
            disabled={loading || (adding && !brief.trim())}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl',
              'bg-room-accent text-white font-semibold text-sm',
              'hover:bg-room-accent/90 active:scale-[0.98] transition-all shadow-lg shadow-room-accent/20',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none'
            )}
          >
            {loading ? (
              <Spinner size={16} />
            ) : (
              <>
                {adding && brief.trim() ? 'Start with context' : 'Looks right — start shooting'}
                <ChevronRight size={15} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Subtle reassurance */}
      <p className="text-xs text-room-muted text-center px-4">
        Context helps AI grade your shots accurately.{' '}
        <span className="text-white/40">Skip if you're in a hurry.</span>
      </p>
    </div>
  )
}
